/**
 * The PRODUCTION CONTEXT, as it rides the URL.
 *
 * The founder's requirement is that you are INSIDE a production and everything after that — times,
 * costs, payroll — is that production's. The context therefore has to be a real, shareable place, not
 * a dropdown's memory: `?production=<uuid>` on the URL means a link opens the same production for the
 * next person, a reload lands back in it, and the back button leaves it.
 *
 * 🔴 The SHELL reads it. A module must not have to: it reads the answer off
 * `window.PinksyGlobalState.state.production` the same way it reads the user (stores/production.ts).
 * The param name is the one the modules already write (`modules/costs`, `modules/productions`), so
 * this is the seam that already existed, given an owner.
 */

/** `?production=` — the one name for the context, everywhere. */
export const PRODUCTION_PARAM = "production";

/**
 * Production ids are uuids (`packages/database/Models/production.ts`). Anything else in the param
 * is not a production, and is dropped rather than published: what the shell puts on the global state
 * is read straight into API paths by every module, so the boundary is here, once, rather than in six
 * modules. A blank `?production=` is nobody, exactly as `modules/costs` already treats it.
 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isProductionId(value: unknown): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value.trim());
}

/** The production a search string names, lower-cased; null when it names none, or names nonsense. */
export function productionIdFromSearch(search: string): string | null {
  const raw = new URLSearchParams(search).get(PRODUCTION_PARAM);
  return isProductionId(raw) ? raw.trim().toLowerCase() : null;
}

/**
 * The same route, carrying the production. Every other query param the route already had is kept —
 * `?section=days` is how the productions module selects a screen, and a nav entry that dropped it
 * would land on the module's default screen instead of the one the row is named after.
 */
export function withProduction(route: string, productionId: string | null): string {
  const [path = "", query] = route.split("?");
  const params = new URLSearchParams(query ?? "");
  if (productionId === null) params.delete(PRODUCTION_PARAM);
  else params.set(PRODUCTION_PARAM, productionId);
  const encoded = params.toString();
  return encoded === "" ? path : `${path}?${encoded}`;
}

/** The same route with the context taken off — where "leave this production" goes. */
export function withoutProduction(route: string): string {
  return withProduction(route, null);
}

/**
 * `?section=` — which SCREEN of a module is open. The shell already reads this everywhere it decides
 * what is active (`navFilter.ts`, `routeGuard.ts`, `Sidebar.vue`); it is named here because it is the
 * one query param that survives a production switch.
 */
export const SECTION_PARAM = "section";

/**
 * SWITCHING production from the top bar: the same SCREEN, the other production.
 *
 * 🔴 Every other query param is dropped, and that is the whole point. `?day=<uuid>` names a shoot day
 * of the production you are leaving, `?budget=<uuid>` one of its budget versions — carried across,
 * they would point the new production's screen at another production's rows, which is either an error
 * page or, far worse, somebody else's figures under this production's name. The path and the section
 * are what "the screen you are on" means; the rest is a pointer into the show you just left.
 */
export function switchProduction(url: string, productionId: string): string {
  const [path = "", query] = url.split("?");
  const section = new URLSearchParams(query ?? "").get(SECTION_PARAM);
  const params = new URLSearchParams();
  if (section !== null) params.set(SECTION_PARAM, section);
  params.set(PRODUCTION_PARAM, productionId);
  return `${path}?${params.toString()}`;
}
